import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  MessageOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Typography,
  notification,
  Checkbox,
  Select,
  Tag
} from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import ajaxService from "../../../services/ajax-service";

import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";

const { Text, Title } = Typography;
const { TextArea } = Input;

const BroadcastNotifications = () => {
  const [form] = Form.useForm();

  const [showModal, setShowModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  const [displayRecipients, setDisplayRecipients] = useState('');

  const [dataSource, setDataSource] = useState({ data: [], current_page: 1, per_page: 10, total: 0 });

  const [searchTerm, setSearchTerm] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const debounceTimer = useRef(null);

  const [users, setUsers] = useState([]); // for recipients select
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isPremiumChecked, setIsPremiumChecked] = useState(false);
  const [isFreeChecked, setIsFreeChecked] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userTypeFilter, setUserTypeFilter] = useState(''); // for user dropdown filter // for user dropdown filter
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [modalWidth, setModalWidth] = useState('50%');

  const pageSize = 10;

  const getStatusText = (stateId) => {
    switch (stateId) {
      case 1: return 'Draft';
      case 2: return 'Scheduled';
      case 3: return 'Sent';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (stateId) => {
    switch (stateId) {
      case 3: return 'green';
      case 1: return 'blue';
      case 2: return 'orange';
      default: return 'default';
    }
  };

  const getRecipientsText = (recipients) => {
    if (!recipients) return 'N/A';
    const rec = typeof recipients === 'string' ? JSON.parse(recipients) : recipients;
    if (rec.includes('all')) return 'All Users';
    return `${rec.length} Users`;
  };

  const columns = [
    {
      title: "SNo",
      dataIndex: "id",
      key: "id",
      width: 80,
      fixed: 'left',
      render: (text, record, index) => (
        (dataSource.current_page - 1) * dataSource.per_page + index + 1
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (title) => <Text>{title}</Text>,
    },
    {
      title: "Message",
      key: "message",
      width: 300,
      ellipsis: true,
      render: (_, record) => <Text>{record.message?.substring(0, 50)}...</Text>,
    },
    {
      title: "Recipients",
      key: "recipients",
      width: 150,
      render: (_, record) => <Text>{getRecipientsText(record.recipients)}</Text>,
    },
    {
      title: "Status",
      key: "status_id",
      width: 120,
      sorter: (a, b) => (a.status_id || 0) - (b.status_id || 0),
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => (
        <Tag color={getStatusColor(record.status_id)}>
          {getStatusText(record.status_id)}
        </Tag>
      ),
    },
    {
      title: "Sent At",
      key: "sent_at",
      width: 180,
      sorter: (a, b) => {
        if (!a.sent_at && !b.sent_at) return 0;
        if (!a.sent_at) return 1;
        if (!b.sent_at) return -1;
        return new Date(a.sent_at) - new Date(b.sent_at);
      },
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => <Text>{record.sent_at || 'N/A'}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--primary-purple)',
              border: '1px solid var(--primary-purple)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<EyeOutlined style={{ fontSize: '12px' }} />}
            onClick={() => openViewModal(record)}
          />
        </div>
      ),
    },
  ];

  const fetchUsers = async (search = '', userType = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('name', search);
      if (userType) params.append('user_type', userType);
      params.append('per_page', '50');
      
      const url = `/users?${params.toString()}`;
      const response = await ajaxService.get(url);
      if (response.success) {
        setUsers(response.data.data || response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchBroadcasts = async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) {
        params.append('search', search.trim());
      }
      params.append('page', page.toString());
      params.append('per_page', pageSize.toString());

      const queryString = params.toString();
      console.log('Search payload (query params):', queryString); // Debug log to verify search param is included

      const response = await ajaxService.get(`/broad-cast-notifications?${queryString}`);
      if (response.success) {
        setDataSource({
          data: response.data.data || [],
          current_page: response.data.current_page || page,
          per_page: response.data.per_page || pageSize,
          total: response.data.total || 0
        });
      } else {
        setDataSource({ data: [], current_page: 1, per_page: pageSize, total: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch broadcasts:", error);
      setDataSource({ data: [], current_page: 1, per_page: pageSize, total: 0 });
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchBroadcasts(1, '');
  }, []);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  // Calculate table scroll width using standardized utility
  // Table columns total: 80 + 200 + 300 + 150 + 120 + 180 + 80 = 1110px (using 1400 for safety)
  const calculateTableWidth = useCallback(() => {
    return calculateTableScrollX(sidebarCollapsed, 1000, 1400, 40);
  }, [sidebarCollapsed]);

  const [tableScrollX, setTableScrollX] = useState(() => calculateTableScrollX(false, 1000, 1400, 40));

  // Recalculate on window resize and sidebar toggle
  useEffect(() => {
    const handleResize = () => {
      setTableScrollX(calculateTableWidth());
      setModalWidth(calculateModalWidth('50%'));
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateTableWidth]);

  const onFinish = async (values) => {
    const formData = new FormData();
    formData.append('title', values.title);
    formData.append('message', values.message);
    if (values.image) {
      formData.append('image', values.image[0].originFileObj);
    }

    let recipients = [];
    // Add checkbox values to recipients
    if (isAllChecked) {
      recipients.push('all');
    }
    if (isPremiumChecked) {
      recipients.push('paid-user');
    }
    if (isFreeChecked) {
      recipients.push('free-user');
    }
    // Add selected users from dropdown
    if (selectedUsers && selectedUsers.length > 0) {
      recipients = [...recipients, ...selectedUsers];
    }
    recipients.forEach(recipient => {
      formData.append('recipients[]', recipient);
    });

    // create
    const response = await ajaxService.post("/broad-cast-notifications", formData);

    if (response.success) {
      handleCancel();
      fetchBroadcasts(dataSource.current_page, searchTerm);
      notification.open({
        message: response.message || "Broadcast added successfully!",
        icon: <CheckCircleOutlined style={{ color: "var(--primary-purple)" }} />,
      });
    } else {
      notification.open({
        message: "Unable to save broadcast notification.",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
  };

  const openViewModal = async (record) => {
    await fetchUsers();
    setIsViewMode(true);
    setShowModal(true);
    const rec = typeof record.recipients === 'string' ? JSON.parse(record.recipients) : record.recipients || [];
    const isAll = rec.includes('all');
    setDisplayRecipients(isAll ? 'All Users' : rec.map(id => {
      const user = users.find(u => u.id === parseInt(id));
      return user ? user.name || user.email : id;
    }).join(', '));
    form.setFieldsValue({
      title: record.title,
      message: record.message,
      image: record.image_url ? [{ uid: -1, name: record.image_url, status: 'done', url: record.image_url }] : [],
    });
  };

  const openCreateModal = () => {
    setIsViewMode(false);
    setDisplayRecipients('');
    setIsAllChecked(false);
    setIsPremiumChecked(false);
    setIsFreeChecked(false);
    setUserTypeFilter('');
    setSelectedUsers([]);
    fetchUsers('', '');
    form.resetFields();
    setShowModal(true);
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed: " + errorInfo);
  };

  const handleSearch = ({ search }) => {
    const query = search ? search.trim() : '';
    console.log('Search term:', query); // Debug log to verify search input
    setSearchTerm(query);
    fetchBroadcasts(1, query);
  };

  // Debounced search handler
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      handleSearch({ search: searchValue });
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleChangePage = (page) => {
    fetchBroadcasts(page, searchTerm);
  };

  const handleCancel = () => {
    setShowModal(false);
    setIsViewMode(false);
    setDisplayRecipients('');
    setIsAllChecked(false);
    setIsPremiumChecked(false);
    setIsFreeChecked(false);
    setUserTypeFilter('');
    setSelectedUsers([]);
    form.resetFields();
  };

  const handleAllChange = (e) => {
    setIsAllChecked(e.target.checked);
    if (e.target.checked) {
      setIsPremiumChecked(false);
      setIsFreeChecked(false);
      setUserTypeFilter('');
      setSelectedUsers([]);
      fetchUsers('', '');
    }
  };

  const handlePremiumChange = (e) => {
    setIsPremiumChecked(e.target.checked);
    if (e.target.checked) {
      setIsAllChecked(false);
      setIsFreeChecked(false);
      setUserTypeFilter('paid-user');
      setSelectedUsers([]);
      fetchUsers('', 'paid-user');
    }
  };

  const handleFreeChange = (e) => {
    setIsFreeChecked(e.target.checked);
    if (e.target.checked) {
      setIsAllChecked(false);
      setIsPremiumChecked(false);
      setUserTypeFilter('free-user');
      setSelectedUsers([]);
      fetchUsers('', 'free-user');
    }
  };

  const handleUserSelectChange = (value) => {
    setSelectedUsers(value);
  };



  const renderModal = (
    <Modal
      className="modal-overlay"
      width={modalWidth}
      open={showModal}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <div style={{ 
        padding: "var(--spacing-2xl) var(--spacing-xl)",
        background: 'var(--dark-bg-card)',
        borderRadius: 'var(--radius-lg)'
      }}>
        {/* Modal Header */}
        <div style={{ 
          marginBottom: 'var(--spacing-2xl)', 
          textAlign: "center",
          paddingBottom: 'var(--spacing-lg)',
          borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
        }}>
          <Title id="modal-title" level={3} style={{ 
            color: 'var(--text-white)', 
            margin: 0,
            fontFamily: 'Poppins-Bold',
            fontSize: '1.5rem'
          }}>
            {isViewMode ? `View Broadcast` : "Add New Broadcast Notification"}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Title</span>}
                name="title"
                rules={[{ required: true, message: "Title is required" }]}
              >
                <Input
                  placeholder="Enter title"
                  allowClear
                  className="input-field  title-input"
                  
                  disabled={isViewMode}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Message</span>}
                name="message"
                rules={[{ required: true, message: "Message is required" }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter message"
                  allowClear
                  className="input-field"
                  disabled={isViewMode}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Image</span>}
                name="image"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) return e;
                  return e?.fileList || [];
                }}
              >
                <Upload
                  name="image"
                  listType="picture"
                  maxCount={1}
                  beforeUpload={() => false}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />} className="btn-secondary">Select Image</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row> */}

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item label={<span style={{ 
                color: 'var(--text-white)', 
                fontWeight: 600,
                fontSize: '0.95rem',
                display: 'block',
                marginBottom: 'var(--spacing-xs)'
              }}>Recipients</span>}>
                {isViewMode ? (
                  <Text strong style={{ color: 'var(--text-white)' }}>{displayRecipients}</Text>
                ) : (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'center' }}>
                      <Checkbox 
                        onChange={handleAllChange} 
                        checked={isAllChecked}
                        style={{ color: 'var(--text-white)' }}
                      >
                        All
                      </Checkbox>
                      <Checkbox 
                        onChange={handlePremiumChange} 
                        checked={isPremiumChecked}
                        style={{ color: 'var(--text-white)' }}
                      >
                        Premium
                      </Checkbox>
                      <Checkbox 
                        onChange={handleFreeChange} 
                        checked={isFreeChecked}
                        style={{ color: 'var(--text-white)' }}
                      >
                        Free
                      </Checkbox>
                    </div>
                    {!isAllChecked && (
                      <Select
                        showSearch
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Search and select users by name"
                        value={selectedUsers}
                        onChange={handleUserSelectChange}
                        onSearch={(value) => fetchUsers(value, userTypeFilter)}
                        className="input-field"
                        disabled={isViewMode}
                        filterOption={false}
                        defaultActiveFirstOption={false}
                      >
                        {users.map(user => (
                          <Select.Option key={user.id} value={user.id.toString()}>{user.name || user.email}</Select.Option>
                        ))}
                      </Select>
                    )}
                  </Space>
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* Modal Footer */}
          <div style={{ 
            textAlign: "center", 
            marginTop: 'var(--spacing-2xl)',
            paddingTop: 'var(--spacing-lg)',
            borderTop: '1px solid rgba(254, 2, 125, 0.2)',
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              key="cancel"
              onClick={handleCancel}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button
                key="finish"
                type="primary"
                onClick={form.submit}
                className="btn-primary"
                style={{ 
                  minWidth: '120px',
                  height: '40px'
                }}
              >
                Add
              </Button>
            )}
          </div>
        </Form>
      </div>
    </Modal>
  );

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <h1 className="module-title">Broadcast Management</h1>
        <p className="module-subtitle">Send notifications and messages to users</p>
      </div>

      {/* Statistics Cards */}
      {/* <div className="stats-grid">
        <div className="stats-card">
          <div className="stats-value">{dataSource.total || 0}</div>
          <div className="stats-label">Total Broadcasts</div>
          <MessageOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{dataSource.data?.filter(b => b.status_id === 3).length || 0}</div>
          <div className="stats-label">Sent</div>
          <SendOutlined style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{dataSource.data?.filter(b => b.status_id === 2).length || 0}</div>
          <div className="stats-label">Scheduled</div>
          <CalendarOutlined style={{ color: 'var(--accent-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{dataSource.data?.filter(b => b.status_id === 1).length || 0}</div>
          <div className="stats-label">Drafts</div>
          <EditOutlined style={{ color: 'var(--text-secondary)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
      </div> */}

      {/* Filters and Search */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-item">
            <Input
              placeholder="Search broadcasts..."
              prefix={<SearchOutlined style={{ color: 'var(--text-tertiary)' }} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={(e) => {
                setSearchValue(e.target.value);
                handleSearch({ search: e.target.value });
              }}
              className="input-field"
              allowClear
            />
          </div>
          <div className="filter-actions">
            <Tooltip title="Create a new broadcast message">
              <Button
                className="btn-primary"
                icon={<MessageOutlined />}
                onClick={openCreateModal}
                style={{ flex: 1 }}
              >
                New Broadcast
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Broadcasts Table */}
      <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Table
          columns={columns}
          dataSource={dataSource.data || []}
          className="data-table"
          rowKey="id"
          pagination={{
            onChange: handleChangePage,
            current: dataSource.current_page,
            pageSize: dataSource.per_page,
            total: dataSource.total,
            className: "data-table-pagination",
            showSizeChanger: false,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} broadcasts`,
            size: 'small'
          }}
          scroll={{ x: tableScrollX }}
          size="small"
          bordered={false}
        />
      </div>

      {renderModal}
    </div>
  );
};

export default BroadcastNotifications;
